<template>
  <div class="search-section">
    <div class="container">
      <div class="row align-items-center">
        <div class="col-3 d-none d-lg-block">
          <button class="categories-toggle">
            <font-awesome-icon icon="fas fa-th-large" />
            Categories
          </button>
        </div>
        <form @submit.prevent="goToSearchPage" class="col search-box">
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search products..."
            @blur="showSuggestions = false"
            @input="debounceSearch"
          />
          <font-awesome-icon
            class="search-icon"
            icon="fas fa-search"
            @click="goToSearchPage"
          />
          <!-- Dropdown for search results -->
          <div class="search-result-wrapper">
            <ul
              v-if="
                searchSuggestions.length &&
                searchQuery.trim() &&
                showSuggestions
              "
              class="search-dropdown"
            >
              <li
                v-for="(book, b) in searchSuggestions"
                :key="`search-${b}`"
                @mousedown.prevent="selectResult(book)"
              >
                <img
                  :src="
                    book.images?.length
                      ? `${BASE_IMAGE_PREFIX}/${book.images[0]}`
                      : 'https://libreture.com/static/images/book-placeholder.png'
                  "
                  class="suggestion-book-image"
                  alt="Book Cover"
                />
                <span>{{ book.title }}</span>
              </li>
            </ul>
          </div>
        </form>
        <div class="d-none d-md-block col-auto">
          <span class="store-locator" @click="showRequestModal = true">
            <i class="fas fa-map-marker-alt"></i>
            Request a Book
          </span>
        </div>
      </div>
    </div>
    <!-- Book Request Modal -->
    <div
      v-if="showRequestModal"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog">
        <div
          v-if="!isLoggedIn"
          class="modal-content"
        >
          <div class="modal-header">
            <h5 class="modal-title">Please login first.</h5>
            <button
              type="button"
              class="btn-close"
              @click="showRequestModal = false"
            ></button>
          </div>
          <div class="modal-footer">
            <button class="btn btn-primary" @click="goToLogin">
              Login
            </button>
          </div>
        </div>
        <div
          v-else
          class="modal-content"
        >
          <div class="modal-header">
            <h5 class="modal-title">Request a Book</h5>
            <button
              type="button"
              class="btn-close"
              @click="showRequestModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <textarea
              v-model="requestedBook"
              v-if="!requestSuccess"
              class="form-control"
              placeholder="Book name, author etc."
            ></textarea>
            <div v-if="requestSuccess" class="d-block p-5 text-center">
              <h5>Your request is successfully placed.</h5>
            </div>
          </div>
          <div
            v-if="!requestSuccess" class="modal-footer"
          >
            <button class="btn btn-secondary" @click="showRequestModal = false">
              Cancel
            </button>
            <button class="btn btn-primary" @click="requestBook">
              Send Request
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { BASE_URL, BASE_IMAGE_PREFIX, } from "@/globals";
import { analytics } from '@/services/analytics';

export default {
  name: "HeroSection",
  data() {
    return {
      BASE_IMAGE_PREFIX,
      searchQuery: null,
      searchTimeout: null,
      searchSuggestions: [],
      showSuggestions: false,
      showRequestModal: false,
      requestedBook: null,
      errorMessage: null,
      requestSuccess: false,
    };
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
  },
  methods: {
    goToSearchPage() {
      if (!this.searchQuery || this.searchQuery.trim() === "") {
        return;
      }
      this.$router.push(`/search?b=${encodeURIComponent(this.searchQuery)}`);
    },
    async fetchSuggestions() {
      try {
        const response = await axios.get(`${BASE_URL}/api/products`, {
          params: {
            q: this.searchQuery?.trim() || undefined,
            category: undefined,
            author: undefined,
            limit: 3,
          },
        });

        this.searchSuggestions = response.data.books;
        this.showSuggestions = true;
        analytics.search(this.searchQuery)
      } catch (error) {
        console.error("Error fetching books:", error);
      } finally {
        this.isLoading = false;
      }
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      if (this.searchQuery.trim()) {
        this.searchTimeout = setTimeout(() => {
          this.fetchSuggestions();
        }, 500);
      }
    },
    selectResult(book) {
      this.$router.push(`/book/${book._id}`);
    },
    async requestBook() {
      if (!this.requestedBook) return;
      try {
        const token = this.$store.getters.token;
        const headers = {
          Authorization: `Bearer ${token}`,
        };
        const response = await axios.post(
          `${BASE_URL}/api/products/request-book`,
          { description: this.requestedBook },
          { headers }
        );
        if (response.status) {
          this.requestSuccess = true;
        }
      } catch (error) {
        this.errorMessage = error.response.data.message;
      } finally {
        setTimeout(() => {
          this.showRequestModal = false;
          this.requestSuccess = false;
        }, 800);
      }
    },
    goToLogin() {
      this.$router.push('/login');
    },
  },
};
</script>

<style lang='scss' scoped>
/* ################# */
/* Search Section */

.search-section {
  background-color: var(--primary-color);
  padding: 15px 0;
  .search-box {
    position: relative;
    .search-icon {
      position: absolute;
      top: 15px;
      right: 30px;
      font-size: 20px;
      cursor: pointer;
    }
    .search-icon:hover {
      color: var(--primary-color);
    }
  }
}

.categories-toggle {
  color: white;
  background: transparent;
  border: none;
  padding: 8px 15px;
  font-size: 20px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-input {
  border-radius: 30px;
  padding: 12px 25px;
  border: none;
  width: 100%;
}

.search-result-wrapper {
  position: relative;
  .search-dropdown {
    position: absolute;
    width: 100%;
    background: white;
    border: 1px solid #ddd;
    border-radius: 10px;
    max-height: 200px;
    overflow-y: auto;
    list-style: none;
    padding: 0;
    margin: 5px 0 0 0;
    z-index: 5;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .search-dropdown li {
    padding: 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    .suggestion-book-image {
      width: auto;
      height: 40px;
      border-radius: 5px;
      margin-right: 10px;
      object-fit: cover;
    }
  }
  .search-dropdown li:hover {
    background: #f0f0f0;
  }
}

.store-locator {
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  cursor: pointer;
}

@media (max-width: 767px) {
  .categories-toggle {
    display: none;
  }
}
</style>>
